import { CommandByteSequence, ParsedArgs, RuleTree } from '../types';
import * as Command from './commands';
import { convertToConsoleHexString } from './util';

export const checkChunkArgs = (command: CommandByteSequence, rawData: string[], parsedArgs?: ParsedArgs): ParsedArgs => {
  switch (command) {
    case Command.selectCharPrintingMode:
    case Command.selectCharSize:
    case Command.turnUnderlineMode:
    case Command.turnEmphasizedMode:
    case Command.turnWhiteBlackReversePrinting:
    case Command.turn90ClockwiseRotationMode:
    case Command.turnItalicPrintMode:
    case Command.turnUpsideDownPrintMode:
    case Command.turnQuadrupleSizeMode:
    case Command.setCharacterRightSpaceAmount:
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [0];
      }
      break;

    case Command.printAndFeedXLine:
    case Command.defineNVBitImage:
      if (parsedArgs) {
        parsedArgs = convertFirstArgumentTo1(parsedArgs);
      }
      break;

    case Command.cancelUserDefinedCharset:
      if (parsedArgs) {
        const n = Number(parsedArgs[0]);
        parsedArgs[0] = isArgLowerThan(command, ['n', n], 32);
        parsedArgs[0] = isArgGreaterThan(command, ['n', n], 126);
      }
      break;

    case Command.setAbsolutePrintPos:
    case Command.setLeftMargin:
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [0, 0];
      } else if (parsedArgs.length === 1) {
        parsedArgs.push(0);
      }
      break;

    case Command.selectPrintDirection: {
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [0];
      }

      const n = Number(parsedArgs[0]);
      parsedArgs[0] = isArgBetween(command, ['n', n], 3, 48);
      parsedArgs[0] = isArgGreaterThan(command, ['n', n], 51);
      break;
    }

    case Command.setPrintingArea: {
      if (parsedArgs) {
        const [dxL, dxH, dyL, dyH] = parsedArgs.slice(4).map((val) => Number(val));
        if (dxL + dxH * 256 === 0 || dyL + dyH * 256 === 0) {
          parsedArgs = [...parsedArgs.slice(0, 4), 64, 2, 64, 2];
          console.log('[Warning]: printing area width and/or height should not be set to 0, reverting back to default value (entire printable area)');
        }
      }
      break;
    }

    case Command.setLineSpacing:
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs[0] = 54;
      }
      break;

    case Command.setPositionAlignment: {
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [0];
      }

      const n = Number(parsedArgs[0]);
      parsedArgs[0] = isArgBetween(command, ['n', n], 2, 48);
      parsedArgs[0] = isArgGreaterThan(command, ['n', n], 50);
      break;
    }

    case Command.setPrintingAreaWidth:
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [64, 2];
      } else if (parsedArgs.length === 1) {
        parsedArgs.push(2);
      }
      break;

    case Command.selectBitImageMode: {
      if (parsedArgs) {
        // TODO: Implement rule for
        //       `ESC * 1 nL nH` and `ESC * b m n`
        const [m, , nH] = parsedArgs.map((val) => Number(val));
        parsedArgs[0] = isArgBetween(command, ['m', m], 1, 32);
        parsedArgs[0] = isArgGreaterThan(command, ['m', m], 33);
        parsedArgs[2] = isArgGreaterThan(command, ['nH', nH], 3);
      }
      break;
    }

    case Command.printAdvanceRasterGraphic: {
      if (parsedArgs) {
        const [m, n] = parsedArgs.map((val) => Number(val));
        parsedArgs[0] = isArgGreaterThan(command, ['m', m], 72);
        parsedArgs[1] = isArgGreaterThan(command, ['n', n], 72);
      }
      break;
    }

    case Command.defineDownloadedBitImage: {
      if (parsedArgs) {
        const [x, y] = parsedArgs.map((val) => Number(val));
        parsedArgs[0] = isArgGreaterThan(command, ['x', x], 80);
        parsedArgs[1] = isArgGreaterThan(command, ['y', y], 64);
      }
      break;
    }

    case Command.printDownloadedBitImage: {
      if (parsedArgs) {
        const m = Number(parsedArgs[0]);
        parsedArgs[0] = isArgBetween(command, ['m', m], 3, 48);
        parsedArgs[0] = isArgGreaterThan(command, ['m', m], 51);
      }
      break;
    }

    case Command.printNVBitImage: {
      if (parsedArgs) {
        const [, m] = parsedArgs.map((val) => Number(val));

        parsedArgs = convertFirstArgumentTo1(parsedArgs);
        parsedArgs[1] = isArgBetween(command, ['m', m], 3, 48);
        parsedArgs[1] = isArgGreaterThan(command, ['m', m], 51);
      }
      break;
    }

    case Command.selectCodeConversion: {
      if (parsedArgs) {
        const [pL, pH, fn, m] = parsedArgs.map((val) => Number(val));

        switch (fn) {
          case 48:
            parsedArgs[0] = isArgShouldBe(command, ['pl', pL], 2);
            parsedArgs[1] = isArgShouldBe(command, ['ph', pH], 0);
            parsedArgs[3] = isArgBetween(command, ['m', m], 2, 49);
            parsedArgs[3] = isArgLowerThan(command, ['m', m], 1);
            parsedArgs[3] = isArgGreaterThan(command, ['m', m], 50);
            break;

          default:
            break;
        }
      }
      break;
    }

    case Command.specifyGraphicsData:
    case Command.specifyGraphicsDataAlternative: {
      if (parsedArgs) {
        const [pL, pH, m, fn] = parsedArgs.map((val) => Number(val));

        switch (fn) {
          case 0:
          case 48:
            break;

          /**
           * Print the graphic data in the print buffer
           */
          case 2:
          case 50:
            parsedArgs[0] = isArgShouldBe(command, ['pl', pL], 2);
            parsedArgs[1] = isArgShouldBe(command, ['ph', pH], 0);
            parsedArgs[2] = isArgShouldBe(command, ['m', m], 48);
            break;

          case 3:
          case 51:
            break;

          case 64:
            break;

          case 65:
            break;

          case 66:
            break;

          case 67:
            break;

          case 69:
            break;

          /**
           * Stores the raster graphic data in the print buffer memory
           */
          case 112: {
            parsedArgs.push(...rawData.slice(4, 12).map((val) => val.charCodeAt(0)));
            const [a, bx, by, c, , xH, , yH] = parsedArgs.slice(4) as number[];

            const dataSize = pL + pH * 256;
            if (dataSize > 65535 || dataSize < 11) {
              throw new Error('Data size for storing graphic data command is not in range of 11 - 65535');
            }

            parsedArgs[2] = isArgShouldBe(command, ['m', m], 48);
            parsedArgs[4] = isArgShouldBe(command, ['a', a], 48);
            parsedArgs[5] = isArgGreaterThan(command, ['bx', bx], 2);
            parsedArgs[5] = isArgLowerThan(command, ['bx', bx], 1);
            parsedArgs[6] = isArgGreaterThan(command, ['by', by], 2);
            parsedArgs[6] = isArgLowerThan(command, ['by', by], 1);
            parsedArgs[7] = isArgShouldBe(command, ['c', c], 49);
            parsedArgs[9] = isArgGreaterThan(command, ['xh', xH], 3);

            if (by === 1) {
              parsedArgs[11] = isArgGreaterThan(command, ['yh', yH], 5);
            } else if (by === 2) {
              parsedArgs[11] = isArgGreaterThan(command, ['yh', yH], 2);
            }
            break;
          }

          default:
            break;
        }
      }
      break;
    }

    case Command.printRasterBitImage: {
      if (parsedArgs) {
        const [m, , , , yH] = parsedArgs.map((val) => Number(val));

        parsedArgs[0] = isArgBetween(command, ['m', m], 3, 48);
        parsedArgs[0] = isArgGreaterThan(command, ['m', m], 51);
        parsedArgs[4] = isArgGreaterThan(command, ['yh', yH], 8);
      }
      break;
    }

    case Command.setBarcodeHeight:
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [216];
      }

      parsedArgs = convertFirstArgumentTo1(parsedArgs);
      break;

    case Command.setBarcodeWidth: {
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [3];
      }

      const n = Number(parsedArgs[0]);
      parsedArgs[0] = isArgLowerThan(command, ['n', n], 2);
      parsedArgs[0] = isArgGreaterThan(command, ['n', n], 6);
      break;
    }

    case Command.selectHRICharacterPrintPos: {
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [0];
      }

      const n = Number(parsedArgs[0]);
      parsedArgs[0] = isArgBetween(command, ['n', n], 3, 48);
      parsedArgs[0] = isArgGreaterThan(command, ['n', n], 51);
      break;
    }

    case Command.selectHRICharacterFont: {
      if (!parsedArgs || parsedArgs.length === 0) {
        parsedArgs = [0];
      }

      const n = Number(parsedArgs[0]);
      parsedArgs[0] = isArgBetween(command, ['n', n], 1, 48);
      parsedArgs[0] = isArgGreaterThan(command, ['n', n], 49);
      break;
    }

    case Command.printBarcode: {
      if (parsedArgs) {
        const m = Number(parsedArgs[0]);

        parsedArgs[0] = isArgBetween(command, ['m', m], 16, 65);
        parsedArgs[0] = isArgGreaterThan(command, ['m', m], 79);

        if (m >= 65) {
          parsedArgs.push(...rawData.slice(1, 2).map((val) => val.charCodeAt(0)));
        }
      }
      break;
    }

    case Command.print2dCodes: {
      if (parsedArgs) {
        const [, , cn, fn] = parsedArgs.map((val) => Number(val));

        parsedArgs[2] = isArgLowerThan(command, ['cn', cn], 48);
        parsedArgs[2] = isArgGreaterThan(command, ['cn', cn], 54);
        parsedArgs[3] = isArgLowerThan(command, ['fn', fn], 65);
        parsedArgs[3] = isArgGreaterThan(command, ['fn', fn], 82);

        switch (cn) {
          case 48:
            switch (fn) {
              case 65: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [n] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgGreaterThan(command, ['n', n], 30);
                break;
              }

              case 66: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [n] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgBetween(command, ['n', n], 0, 3);
                parsedArgs[4] = isArgGreaterThan(command, ['n', n], 90);
                break;
              }

              case 67: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));
                break;
              }

              case 68: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));
                break;
              }

              case 69: {
                parsedArgs.push(...rawData.slice(4, 6).map((val) => val.charCodeAt(0)));

                const [m, n] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgLowerThan(command, ['m', m], 48);
                parsedArgs[4] = isArgGreaterThan(command, ['m', m], 49);

                if (m === 48) {
                  parsedArgs[5] = isArgLowerThan(command, ['n', n], 48);
                  parsedArgs[5] = isArgGreaterThan(command, ['n', n], 56);
                } else {
                  parsedArgs[5] = isArgLowerThan(command, ['n', n], 1);
                  parsedArgs[5] = isArgGreaterThan(command, ['n', n], 40);
                }
                break;
              }

              case 70: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [n] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgGreaterThan(command, ['n', n], 1);
                break;
              }

              case 80: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));
                break;
              }

              case 81: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [m] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgShouldBe(command, ['m', m], 48);
                break;
              }

              case 82: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [m] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgShouldBe(command, ['m', m], 48);
                break;
              }

              default:
                break;
            }
            break;

          case 49:
            switch (fn) {
              case 65: {
                parsedArgs.push(...rawData.slice(4, 6).map((val) => val.charCodeAt(0)));

                const [n1] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgLowerThan(command, ['n1', n1], 49);
                parsedArgs[4] = isArgGreaterThan(command, ['n1', n1], 51);
                break;
              }

              case 67: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));
                break;
              }

              case 69: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [n] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgLowerThan(command, ['n', n], 48);
                parsedArgs[4] = isArgGreaterThan(command, ['n', n], 51);
                break;
              }

              case 80: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));
                break;
              }

              case 81: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [m] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgShouldBe(command, ['m', m], 48);
                break;
              }

              case 82: {
                parsedArgs.push(...rawData.slice(4, 5).map((val) => val.charCodeAt(0)));

                const [m] = parsedArgs.slice(4) as number[];
                parsedArgs[4] = isArgShouldBe(command, ['m', m], 48);
                break;
              }

              default:
                break;
            }
            break;

          default:
            break;
        }
      }
      break;
    }
  }

  if (!parsedArgs || parsedArgs.length === 0) {
    throw new Error(`Required argument for [${convertToConsoleHexString(command)}] command is missing`);
  }

  return parsedArgs;
};

const convertFirstArgumentTo1 = (parsedArgs: ParsedArgs) => {
  if (parsedArgs[0] === 0) return [1, ...parsedArgs.slice(1)];
  else return parsedArgs;
};

const isArgShouldBe = (command: CommandByteSequence, arg: [string, number], target: number) => {
  const [argString, argValue] = arg;
  if (argValue !== target) {
    console.log(`[Warning]: argument ${argString} for ${convertToConsoleHexString(command)} command is not ${target}, changing it to ${target}`);
    return target;
  }

  return argValue;
};

const isArgBetween = (command: CommandByteSequence, arg: [string, number], low: number, high: number) => {
  const [argString, argValue] = arg;
  if (argValue > low && argValue < high) {
    console.log(`[Warning]: argument ${argString} for ${convertToConsoleHexString(command)} command is between ${low} - ${high}, rounding it to the closest one`);

    const middle = (high - low) / 2;
    return Math.abs(argValue - middle) > middle ? high : low;
  }

  return argValue;
};

const isArgGreaterThan = (command: CommandByteSequence, arg: [string, number], high: number) => {
  const [argString, argValue] = arg;
  if (argValue > high) {
    console.log(`[Warning]: argument ${argString} for ${convertToConsoleHexString(command)} command is more than ${high}, rounding it to ${high}`);
    return high;
  }

  return argValue;
};

const isArgLowerThan = (command: CommandByteSequence, arg: [string, number], low: number) => {
  const [argString, argValue] = arg;
  if (argValue < low) {
    console.log(`[Warning]: argument ${argString} for ${convertToConsoleHexString(command)} command is less than ${low}, rounding it to ${low}`);
    return low;
  }

  return argValue;
};

const mergeRuleTree = (commandTree: RuleTree, newNode: RuleTree) => {
  if (newNode !== null && typeof newNode === 'object') {
    for (const [byte, value] of Object.entries(newNode)) {
      let matchFound = false;

      if (commandTree[byte]) {
        mergeRuleTree(commandTree[byte], value);
        matchFound = true;
        break;
      }

      if (!matchFound) {
        Object.assign(commandTree, newNode);
      }
    }
  }
};

export const findRuleTree = (commandTree: RuleTree, byteSequenceToFind: string[]): RuleTree => {
  let clonedCommandTree = commandTree;

  for (const byte of byteSequenceToFind) {
    if (byte in clonedCommandTree) {
      const node = clonedCommandTree[byte];

      clonedCommandTree = {};
      clonedCommandTree = node;
    } else {
      clonedCommandTree = {};
      break;
    }
  }

  return clonedCommandTree;
};

export const generateRuleTree = (): RuleTree => {
  const combinedCommands = {
    ...Command.basicCommand,
    ...Command.characterCommand,
    ...Command.layoutCommand,
    ...Command.graphicCommand,
    ...Command.barcodeCommand,
    ...Command.miscCommand,
  };

  const fullCommandsList: RuleTree = {};
  for (const [command, description] of Object.entries(combinedCommands)) {
    const commandList = command.split('').reverse();

    let commandTree: RuleTree = {
      [commandList.shift() as string]: {
        content: description,
      },
    };

    for (const byte of commandList) {
      const tempCommandTree = commandTree;

      commandTree = {};
      commandTree[byte] = tempCommandTree;
    }

    mergeRuleTree(fullCommandsList, commandTree);
  }

  return fullCommandsList;
};
